import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth, usePermission } from '@saas-kit/lib';
import {
  Card,
  CardContent,
  ErrorBanner,
  PageHeader,
} from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { ApiKeyCreateForm } from './ApiKeyCreateForm';
import { ApiKeyList } from './ApiKeyList';
import { ApiKeySecretCard } from './ApiKeySecretCard';
import {
  listApiKeyScopes,
  listApiKeys,
  type ApiKeyCreated,
} from './api';

export function ApiKeysPage(): JSX.Element {
  const { t } = useTranslation('apiKeys');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canManage = usePermission('api_keys.manage');
  const [latestCreated, setLatestCreated] = useState<ApiKeyCreated | null>(null);

  const keysQuery = useQuery({
    queryKey: ['tenant-api-keys', selectedTenant?.tenant_id],
    queryFn: listApiKeys,
    enabled: Boolean(selectedTenant?.tenant_id && canManage),
  });
  const scopesQuery = useQuery({
    queryKey: ['tenant-api-key-scopes'],
    queryFn: listApiKeyScopes,
    staleTime: 60_000,
    enabled: Boolean(selectedTenant?.tenant_id && canManage),
  });

  const keys = keysQuery.data ?? [];
  const activeKeys = keys.filter((key) => key.revoked_at === null).length;
  const revokedKeys = keys.length - activeKeys;

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label={t('metrics.total')} value={String(keys.length)} />
        <MetricCard label={t('metrics.active')} value={String(activeKeys)} />
        <MetricCard label={t('metrics.revoked')} value={String(revokedKeys)} />
      </div>

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canManage ? (
        <ErrorBanner>{t('errors.manage_denied')}</ErrorBanner>
      ) : (
        <>
          <ApiKeyCreateForm
            scopes={scopesQuery.data ?? []}
            scopesError={scopesQuery.isError ? scopesQuery.error : null}
            onCreated={setLatestCreated}
          />
          {latestCreated ? (
            <ApiKeySecretCard
              secret={latestCreated.secret}
              onDismiss={() => setLatestCreated(null)}
            />
          ) : null}
          <ApiKeyList
            keys={keys}
            isLoading={keysQuery.isLoading}
            isError={keysQuery.isError}
            error={keysQuery.error}
          />
        </>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}
